import { useQuery } from '@tanstack/react-query';
import { useAdminStore } from '../../store/adminStore';
import { isForbiddenError, postGraphQL } from './client';

export const CHAT_ACCESS_QUERY = `
  query SupportChatAccess($from: DateTime!, $to: DateTime!) {
    chatMetrics(from: $from, to: $to) {
      sessions
    }
  }
`;

/**
 * Ant answers chatMetrics for any admin holding a chat support role (Support
 * Viewer, Agent or Lead) anywhere, and "forbidden" for everyone else
 * (Ant.Chat.Metrics.compute -> Ant.Chat.Access.any?). A one-second range
 * keeps this permission probe small; the count itself does not grant access
 * (an authorized admin can have an empty queue).
 *
 * Not issueMetrics: since ant restricted the issues report to Support Leads
 * granted on the support root, probing it hid the console from every
 * Support Agent and Viewer. Not chatSupportStatus: it is public to signed-in
 * callers, so it cannot serve as a gate.
 */
export async function fetchChatAccess(): Promise<boolean> {
  const to = new Date();
  const from = new Date(to.getTime() - 1000);
  try {
    const data = await postGraphQL<{ chatMetrics: { sessions: number } | null }>(
      CHAT_ACCESS_QUERY,
      { from: from.toISOString(), to: to.toISOString() }
    );
    return data.chatMetrics != null;
  } catch (error) {
    if (isForbiddenError(error)) return false;
    throw error;
  }
}

/** Shared by the global console route and sidebar, independent of system selection. */
export function useChatAccess() {
  const admin = useAdminStore((state) => state.admin.admin);
  const signedIn = Boolean(admin?.id && admin?.email);
  const query = useQuery({
    queryKey: ['supportChatAccess', admin?.id, admin?.email],
    queryFn: fetchChatAccess,
    enabled: signedIn,
    staleTime: 30000,
    gcTime: 0,
    retry: false,
  });
  return {
    canAccess: signedIn && query.data === true,
    loading: signedIn && query.isPending && !query.isError,
    error: query.error,
    retry: query.refetch,
  };
}
